/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { useContext, useMemo } from 'react';

import { BlueprintInstanceType, CORNER_THICKNESS, CORNER_WIDTH, Dimension, FLAT_TRIM_HALF_WIDTH, islandHasCornerTrims, ReferenceMap } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { convertRect2Vectors } from '@r-core/helpers';
import { CreateCornerPattern } from '../symbols';
import { DisplayMode } from '@r-core';
import { DrawPanelDimensions } from '../draw-dimensions/draw-panel-dimensions';
import { filterPassthroughDimensions } from '../helpers/filter-passthrough-dimensions';
import { svgPolygonFromVectors } from '../helpers/svg-polygon-from-vectors';
import { RenderBreakpointsCountertop } from './render-breakpoints-countertop';
import { RenderBreakpointsPanel } from './render-breakpoints-panel';
import { renderTracking } from '@r-services/render-tracking';
import { RenderPanelShadows } from './render-panel-shadows';
import { RenderPanelItemGroups } from './render-panel-item-groups';
import { useFps } from '@r-hooks';

import { addContextMenu, ReferenceEntryType } from '../../context-menu';
import { DevShowIntersectionAreas, DimensionLine } from '@r-components';
import { Layer, ShowDimensions } from '@r-core/types';

import '../../app/styles/blueprint-component.scss';
import { RenderPanelMask } from './render-panel-mask';
import { RenderSkuLayer } from './render-sku-layer';

export type RenderPanelProps = {
	islandId: string;
	panelId: string;
	offsetX?: number;
	offsetY?: number;
	showDimensions?: boolean;
	showApps?: boolean;
	showSkuLayer?: boolean;
};

export const RenderPanel = (props: RenderPanelProps): React.ReactElement => {
	const { controller: { options, navigation, kitchen }} = useContext(AppContext);
	const {
		panelId,
		islandId,
		offsetX = 0,
		offsetY = 0,
		showDimensions = true,
		showApps = true,
		showSkuLayer = true
	} = props;

	renderTracking.incrementRender('RenderPanel');

	const referenceMap = useMemo<ReferenceMap>(() => {
		// console.log('usePanel:useMemo:getReferenceMap', panelId);
		return kitchen.getReferenceMap(panelId);
	}, [panelId]);

	const { island, panel } = referenceMap;

	useFps(`render-panel-${island?.sku.toLowerCase() ?? 'na'}-${panel.label?.toLowerCase() ?? 'na'}`, options.fps);

	/* get countertop details from panel perspective */
	const sidePoints = panel.getSidePoints({ includeCountertop: options.showCountertops, translate: false, rotate: false });
	const countertop = island.getCountertop();
	const countertopCenter = { x: sidePoints ? sidePoints.center.x : 0, y: countertop.center.y };
	const countertopSize = {
		// calculate width using sidepoints
		width: sidePoints ? sidePoints.corners[1].x - sidePoints.corners[0].x : 0,
		// we only use width and depth for panel view
		height: countertop.size.height,
		// countertop thickness is the same accross all panel views
		depth: countertop.size.depth
	};
	// console.log('countertop', { size: countertopSize, center: countertopCenter, corners: sidePoints?.corners });

	// panel sides, don't need to execute again if countertop is not showing
	const panelSize = {
		width: panel.size.width,
		height: panel.size.height,
		depth: panel.size.depth
	};
	// console.log('panel', { label: panel.label, size: panelSize, center: sidePoints?.center, corners: sidePoints?.corners });

	// surround
	const materialAsset = island.panels.surroundMaterial;
	const materialScale = materialAsset.scale;
	const blueprintToApplianceOpacity = (100 - options.applianceOpacity) / 100;

	const leftTrim = island.panelTrims.findByPanelIds(panelId, panel.connections.left.targetPanelId);
	const rightTrim = island.panelTrims.findByPanelIds(panelId, panel.connections.right.targetPanelId);

	// a flat trim spans the seam, so only its half sits on this panel's elevation
	// A finish with no corner trim hardware draws no corners: the geometry used to render regardless,
	// filled from a cornerTexture that family has none of, which resolves to a broken image (#467).
	const hasCornerTrims = islandHasCornerTrims(island);

	const leftCornerWidth = leftTrim?.isFlat ? FLAT_TRIM_HALF_WIDTH : CORNER_WIDTH;
	const rightCornerWidth = rightTrim?.isFlat ? FLAT_TRIM_HALF_WIDTH : CORNER_WIDTH;
	const cornerEdgeX = (panelSize.width / 2) + CORNER_THICKNESS;
	const leftCornerOffsetX = cornerEdgeX - (leftCornerWidth / 2);
	const rightCornerOffsetX = cornerEdgeX - (rightCornerWidth / 2);

	const translate = {
		x: offsetX,
		y: offsetY
	};

	let showTexture = true;
	let useDimensionBox = true;
	let { showCorners, showCountertops, showAppliances, showCutouts } = options;

	switch (navigation.displayMode) {
		case DisplayMode.Manufacturing:
		case DisplayMode.InlineManufacturing:
		case DisplayMode.SkuOverheadManufacturing:
		case DisplayMode.BlueprintView:
			showTexture = false;
			showCorners = false;
			showCountertops = false;
			showAppliances = false;
			useDimensionBox = false;
			showCutouts = true;
			break;
		case DisplayMode.SkuOverhead:
			showCutouts = true;
			break;
		default:
	}

	const horizontalIntersects = filterPassthroughDimensions({
		panel,
		inside: !showAppliances,
		areas: panel.getHorizontalIntersectionAreas({ inside: !showAppliances, includeDuplicates: false, includeEnd2End: false, minHeight: 0.5 })
			.map((area) => ({
				...area,
				shouldHide: false
				// dimension: kitchen.design.dimensions.find(area.intersectionReferenceIds, area.axis, true) as Dimension
			}))
	});

	// same vent identification approach as filter-passthrough-dimensions.ts, so the chain-collapse pass below can never hide a vent's own dimension
	const ventIds = new Set<string>();
	panel.panelItemGroups.children.forEach((group) => {
		group.items.children.forEach((panelItem) => {
			if (panelItem.item.category?.isVent) ventIds.add(panelItem.id);
		});
	});

	const verticalIntersects = panel.getVerticalIntersectionAreas({ inside: !showAppliances, includeDuplicates: false, includeEnd2End: false, minWidth: 0.5 })
		.map((area) => ({
			...area,
			shouldHide: false
			// dimension: kitchen.design.dimensions.find(area.intersectionReferenceIds, area.axis, true) as Dimension
		}));

	// const processedHorizontalIntersections = [];

	horizontalIntersects.forEach((current, currentIndex) => {
		if (!current.intersectionReferenceIds || current.intersectionReferenceIds.length < 2) {
			return;
		}

		// a vent's own dimension must never be collapsed away by this generic chain-matching pass
		if (current.intersectionReferenceIds.some((id) => ventIds.has(id))) {
			return;
		}

		const [currentFirstId, currentSecondId] = current.intersectionReferenceIds;

		// Find a smaller dimension sharing current's first ID (ensures we only hide the spanning/largest dimension)
		let firstMatch = null;
		for (let i = 0; i < horizontalIntersects.length; i++) {
			if (i === currentIndex) continue;

			const other = horizontalIntersects[i];
			if (!other.shouldHide &&
            other.intersectionReferenceIds &&
            other.intersectionReferenceIds.length >= 2 &&
            other.intersectionReferenceIds[0] === currentFirstId &&
            other.size.width < current.size.width) {
				firstMatch = other;
				break;
			}
		}

		// If we found a first match, create new intersection [matchedSecondId, currentSecondId] and check if it exists
		if (firstMatch) {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const [_matchedFirstId, matchedSecondId] = firstMatch.intersectionReferenceIds;
			const newIntersectionToFind = [matchedSecondId, currentSecondId];

			const newIntersectionExists = horizontalIntersects.some((other) => {
				return other !== current &&
                other.intersectionReferenceIds &&
                other.intersectionReferenceIds.length >= 2 &&
                other.size.width < current.size.width &&
                ((other.intersectionReferenceIds[0] === newIntersectionToFind[0] && other.intersectionReferenceIds[1] === newIntersectionToFind[1]) ||
                 (other.intersectionReferenceIds[0] === newIntersectionToFind[1] && other.intersectionReferenceIds[1] === newIntersectionToFind[0]));
			});

			current.shouldHide = newIntersectionExists;
		}
	});

	verticalIntersects.forEach((current, currentIndex) => {
		if (!current.intersectionReferenceIds || current.intersectionReferenceIds.length < 2) {
			return;
		}

		const [currentFirstId, currentSecondId] = current.intersectionReferenceIds;

		// Find a smaller dimension sharing current's first ID (ensures we only hide the spanning/largest dimension)
		let firstMatch = null;
		for (let i = 0; i < verticalIntersects.length; i++) {
			if (i === currentIndex) continue;

			const other = verticalIntersects[i];
			if (!other.shouldHide &&
            other.intersectionReferenceIds &&
            other.intersectionReferenceIds.length >= 2 &&
            other.intersectionReferenceIds[0] === currentFirstId &&
            other.size.height < current.size.height) {
				firstMatch = other;
				break;
			}
		}

		// If we found a first match, create new intersection [matchedSecondId, currentSecondId] and check if it exists
		if (firstMatch) {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const [_matchedFirstId, matchedSecondId] = firstMatch.intersectionReferenceIds;
			const newIntersectionToFind = [matchedSecondId, currentSecondId];

			const newIntersectionExists = verticalIntersects.some((other) => {
				return other !== current &&
                other.intersectionReferenceIds &&
                other.intersectionReferenceIds.length >= 2 &&
                other.size.height < current.size.height &&
                ((other.intersectionReferenceIds[0] === newIntersectionToFind[0] && other.intersectionReferenceIds[1] === newIntersectionToFind[1]) ||
                 (other.intersectionReferenceIds[0] === newIntersectionToFind[1] && other.intersectionReferenceIds[1] === newIntersectionToFind[0]));
			});

			current.shouldHide = newIntersectionExists;
		}
	});

	const intersectionAreas = horizontalIntersects.concat(verticalIntersects);

	const vectors = {
		panel: convertRect2Vectors({
			layer: Layer.Panel,
			center: { x: 0, y: panel.center.y },
			width: panelSize.width,
			height: panelSize.height,
			angle: 0 // panel.getOriginAngle()
		}),
		countertop: convertRect2Vectors({
			layer: Layer.Countertop,
			center: { x: countertopCenter.x, y: countertopCenter.y },
			width: countertopSize.width,
			height: countertopSize.depth,
			angle: 0 // panel.getOriginAngle()
		}),
		leftCorner: convertRect2Vectors({
			layer: Layer.Corner,
			center: { x: -leftCornerOffsetX, y: panel.center.y },
			width: leftCornerWidth,
			height: panelSize.height,
			angle: 0 // panel.getOriginAngle()
		}),
		rightCorner: convertRect2Vectors({
			layer: Layer.Corner,
			center: { x: rightCornerOffsetX, y: panel.center.y },
			width: rightCornerWidth,
			height: panelSize.height,
			angle: 0 // panel.getOriginAngle()
		})
	}

	// console.log('showpanels-vectors', vectors);

	return (<g id={`panel-${panel.id}`} type="panel-group" transform={`translate(${translate.x}, ${translate.y})`} >
		{ /* create corner pattern def */}
		{ showCorners && hasCornerTrims && <CreateCornerPattern {...{ material: kitchen.material, options }} />}

		{ /* create surround pattern def */}
		{ showTexture && <defs>
			<pattern id="surround-texture" x={-materialScale.width / 2} y="0" height={materialScale.height} width={materialScale.width} patternUnits="userSpaceOnUse">
				<image href={`${options.assetsBaseUrl}/textures/${materialAsset.texture}${options.assetsSasToken}`} x="0" y="0" width={materialScale.width} height={materialScale.height} />
			</pattern>
		</defs>}

		{ /* draw countertop */ }
		{ showCountertops &&
			svgPolygonFromVectors(vectors.countertop, {
				type: 'panel-countertop',
				fill: `url(#counter-texture-${countertop.id})`,
				// mask: `url(#panel-mask-${panelId})`,
				...addContextMenu({ menuKey: BlueprintInstanceType.Countertop, id: countertop.id })
			})
		}

		{ /* no texture required, draw blank panel */ }
		{ (!showTexture) && svgPolygonFromVectors(vectors.panel, {
				key: 'panel-context',
				fill: 'darkgray',
				stroke: 'none',
				opacity: 1,
				...addContextMenu({ menuKey: BlueprintInstanceType.Panel, id: panel.id, center: { x: offsetX, y: offsetY + panel.center.y }})
			})
		}

		{ /* draw panel surround texture over blank panel */ }
		{ showTexture &&
			svgPolygonFromVectors(vectors.panel, {
				key: `panel-surround-${panel.id}`,
				fill: 'url(#surround-texture)',
				stroke: 'none',
				opacity: blueprintToApplianceOpacity,
				...addContextMenu({ menuKey: BlueprintInstanceType.Panel, id: panel.id, center: { x: offsetX, y: offsetY + panel.center.y }})
			})
		}

		{ /* draw corners */ }
		{showCorners && hasCornerTrims && options.corner.show && <>
			{ /* left corner */
			svgPolygonFromVectors(vectors.leftCorner, {
				key: 'panel-corner-left',
				fill: 'url(#corner-texture)',
				...addContextMenu({ menuKey: ReferenceEntryType.Corner, id: leftTrim?.id })
			})}
			{ /* right corner */
			svgPolygonFromVectors(vectors.rightCorner, {
				key: 'panel-corner-right',
				fill: 'url(#corner-texture)',
				...addContextMenu({ menuKey: ReferenceEntryType.Corner, id: rightTrim?.id })
			})}
		</>}

		{ /* display overlap, eg; render shadow layers of other panels */}
		{ options.showOverlap && <RenderPanelShadows {...{ kitchen, islandId, panelId }}/> }

		{ /* load panel item cutout mask def */ }
		{ showCutouts && <RenderPanelMask {...{ islandId, panelId, options: { includeCountertop: true }}} /> }

		{ /* display Appliances */}
		{ showApps && <RenderPanelItemGroups {...{ panelId, island }} /> }

		{ /* render breakpoints */ }
		<RenderBreakpointsPanel {...{ panelId }} />
		{ options.applianceOpacity !== 0 &&
			options.applianceOpacity !== 100 &&
			<RenderBreakpointsCountertop {...{ islandId, panelId }} /> }

		{ /* Developer Layer, visualize intersection areas */ }
		{ options.showDeveloperInsights && <>
			<DevShowIntersectionAreas areas={intersectionAreas}/>
		</>}

		{ /* add panel dimensions */}
		{ options.showPanelOutsideDimensions &&
			showDimensions &&
			<DrawPanelDimensions {...{ panelId, isCountertop: false, displayMode: navigation.displayMode }} />}

		{ /* add panel appliance dimensions (using intersect areas) */}
		{ options.showDimensionsMode !== ShowDimensions.None && options.showApplianceDimensions &&
			intersectionAreas.map((area, i) => {
				return <DimensionLine key={`${i}-${navigation.displayMode}`}
					persist={{ sourceId: area.intersectionReferenceIds[0], targetId: area.intersectionReferenceIds[1], alternateId: area.intersectionReferenceIds[2] }}
					axis={area.axis}
					length={area.axis.z === 0 ? area.size.width : area.size.height}
					canMove={true}
					bBox={{ origin: area.origin, size: area.size }}
					includeLabelBox={useDimensionBox}
					level={true}
					shouldHide = {area.shouldHide}
				/>;
			})
		}

		{ /* Developer Layer / Top */ }
		{ options.showDeveloperInsights && <>
		</>}

		{ /* SKU Layer - Render at TOP z-index when active */}
		{ showSkuLayer && (navigation.displayMode === DisplayMode.SkuOverhead || navigation.displayMode === DisplayMode.SkuOverheadManufacturing) && <RenderSkuLayer panelId={panelId} /> }
	</g>);
};
